import { z } from "zod/v4";

type EnumLiteral<T extends Record<string, string>> = `${T[keyof T]}`;

/**
 * Zod enum over an nswag string enum, typed as the openapi literal union (`"Man" | "Vrouw"`)
 * instead of the nominal enum (`Gender`), so openapi-typed callers can pass their values as-is.
 */
export function isApiEnumValue<T extends Record<string, string>>(enumObject: T) {
  const values: ReadonlySet<string> = new Set(Object.values(enumObject));
  return (value: string): value is EnumLiteral<T> => values.has(value);
}

/**
 * Like isApiEnumValue, but also accepts a [Flags] value the API serializes as a comma-separated
 * list (`"Vijf, Zes"` for a combinatieklas). Typed as the single-member union because the openapi
 * client declares the parameter that way; the API parses the combined string itself.
 */
export function isApiFlagsEnumValue<T extends Record<string, string>>(enumObject: T) {
  const isMember = isApiEnumValue(enumObject);
  return (value: string): value is EnumLiteral<T> =>
    value.length > 0 && value.split(",").every((part) => isMember(part.trim()));
}

export function apiEnum<T extends Record<string, string>>(enumObject: T) {
  // SAFETY: nswag string enums carry their openapi literal as the member value, so the values
  // array is exactly the literal union `${T[keyof T]}`; Object.values only loses that nominally.
  const values = Object.values(enumObject) as [EnumLiteral<T>, ...EnumLiteral<T>[]];
  return z.enum(values);
}
